<template>
  <a v-if="type === 'anchor'" class="button--container" :class="containerClasses" :style="containerStyles" :href="href"
     @mousedown="buttonMouseDownHandler" @mouseup="buttonMouseUpHandler"
     @mouseleave="mouseLeaveHandler" @click="clickHandler">
    <span class="button--label" :class="{'material-icons': icon}">{{ label }}</span>
    <span class="button--ripple" @animationend="buttonRippleAnimationEndHandler"></span>
    <slot></slot>
  </a>
  <button v-else class="button--container" :type="type" :class="containerClasses" :style="containerStyles"
          @mousedown="buttonMouseDownHandler" @mouseup="buttonMouseUpHandler"
          @mouseleave="mouseLeaveHandler" @click="clickHandler">
    <span class="button--label" :class="{'material-icons': icon}">{{ label }}</span>
    <span class="button--ripple" @animationend="buttonRippleAnimationEndHandler"></span>
    <slot></slot>
  </button>
</template>

<script lang="ts">
/**
 * @version 1.1.0
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @copyright Copyright (C) 2021, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 * CHANGELOG:
 *
 * 2021-11-22 (1.0.0) (jhoward)
 *  - Refactor from vanilla JS.
 *
 * 2021-11-29 (1.0.1) (jhoward)
 *  - Support "type" attribute.
 *
 * 2023-01-16 (1.1.0) (jhoward)
 *  - <material-button> now supports type=router-link
 */

import {CSSProperties, defineComponent, PropType} from "vue";

export default defineComponent({
  name: "material-button",
  props: {
    label: {
      type: String,
      required: true
    },
    type: {
      type: String as PropType<'submit' | 'button' | 'reset' | 'anchor' | undefined>,
    },
    icon: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    mod: {
      type: String as PropType<'raised' | 'outline' | 'text' | 'submit'>,
      default: 'raised',
      validator: (value: string) => ['raised', 'outline', 'text', 'submit'].indexOf(value) !== -1
    },
    href: {
      type: String,
      default: '#'
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      active: false,
      mousedown: false,
      animating: false,
      deactivate: false,
      clickTimer: -1,

      ripplePositionX: 0,
      ripplePositionY: 0,
      rippleSize: 0,
      rippleScale: 1,
    };
  },
  computed: {
    containerClasses() {
      return {
        'button--container-raised': this.mod === 'raised' || this.mod === 'submit',
        'button--container-submit': this.mod === 'submit',
        'button--container-outline': this.mod === 'outline',
        'button--container-icon': this.icon,
        'button--container-disabled': this.disabled,
        'button--container-deactivate': this.deactivate,
        'button--container-activate': this.active,
        'button--container-error': this.error,
        'button--container-full_width': this.fullWidth,
        'button--container-loading': this.isLoading
      };
    },
    containerStyles(): CSSProperties {
      const output: {[key: string]: string | number} = {
        '--ripple-pos-x': this.ripplePositionX + 'px',
        '--ripple-pos-y': this.ripplePositionY + 'px',
        '--ripple-size': this.rippleSize + 'px',
        '--ripple-scale': this.rippleScale,
      };

      if (this.isLoading) {
        output['--button-width'] = this.$el.clientWidth + 'px';
      }

      return output;
    },
  },
  methods: {
    buttonRippleAnimationEndHandler: function (event: AnimationEvent) {
      //make sure the mouse button isn't still down
      if (this.mousedown) {
        this.animating = false;
      } else {
        if ('button-ripple-scale-in' === event.animationName.substring(0, 22)) {
          //need to start the deactivate process
          this.deactivate = true;
        } else if ('button-ripple-opacity-out' === event.animationName.substring(0, 25)) {
          //effect animation is complete, let's clean-up
          this.active = false;
          this.deactivate = false;
          this.animating = false;
        }
      }
    },

    buttonMouseUpHandler: function () {
      //don't run the effect for disabled buttons
      if (!this.disabled) {
        this.mousedown = false;

        if (!this.animating) {
          //effect animation is complete, let's clean-up
          this.active = false;
          this.deactivate = true;
        }
      }
    },

    buttonMouseDownHandler: function (event: MouseEvent) {
      //don't run the effect for disabled buttons
      if (!this.disabled) {
        this.mousedown = true;

        clearTimeout(this.clickTimer);

        //reset CSS for button
        this.active = false;
        this.deactivate = false;

        //let the CSS reset settle
        this.clickTimer = window.setTimeout(() => {
          //update CSS properties needed for the effect
          if (this.icon) {
            this.ripplePositionX = Math.round(this.$el.clientWidth / 2);
            this.ripplePositionY = Math.round(this.$el.clientHeight / 2);
            this.rippleSize = Math.max(this.$el.clientWidth, this.$el.clientHeight);
            this.rippleScale = 1;
          } else {
            this.ripplePositionX = event.offsetX;
            this.ripplePositionY = event.offsetY;
            this.rippleSize = (Math.max(this.$el.clientWidth, this.$el.clientHeight) / 1.2);
          }

          //kick off the animation!
          this.active = true;
          this.animating = true;

        }, 16);
      }
    },

    mouseLeaveHandler: function () {
      this.mousedown = false;
      this.active = false;
      this.deactivate = false;
    },

    clickHandler: function (event: Event) {
      if (this.disabled) {
        event.preventDefault();
      }
    },
  }
})
</script>

<style scoped>
.button--container {
  /*set some variable defaults*/
  --ripple-size: 83%;
  --ripple-scale: 2.4;
  --ripple-opacity: 0.24;
  --ripple-pos-x: 50%;
  --ripple-pos-y: 50%;
  --ripple-init-scale: 0.75;
  --button-width: 300px;

  align-items: center;
  background-color: rgb(var(--primary-color, 0, 121, 102));
  border: none;
  border-radius: 4px;
  color: rgb(var(--bg-color, 255, 255, 255));
  display: inline-block;
  justify-content: center;
  letter-spacing: 0.01em;
  min-width: 64px;
  outline: none;
  padding: 12px 16px;
  text-transform: uppercase;
  font-size: 1em;
  font-weight: normal;
  text-decoration: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

.button--container:not(.sheet--handle) {
  position: relative;
}

.button--container:not(.button--container-disabled):hover {
  cursor: pointer;
}

.button--container.button--container-disabled:hover {
  cursor: default;
}

.button--container:active {
  outline: none;
}

.button--container-outline,
.button--container-text {
  background-color: transparent;
  border-color: rgb(0, 121, 102);
  border: 1px solid rgb(var(--primary-color, 0, 121, 102));
  color: rgb(var(--primary-color, 0, 121, 102));
}

.button--container-text {
  border: none;
}

.button--container-icon {
  --ripple-init-scale: 0;

  background-color: transparent;
  border-radius: 50%;
  color: rgb(var(--primary-color, 0, 121, 102));
  height: 48px;
  line-height: 48px;
  min-width: auto;
  padding: 0;
  text-align: center;
  vertical-align: middle;
  width: 48px;
}

.button--container-raised:not(.button--container-disabled) {
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, .12);
  transition: box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1);
}

.button--container-raised:not(.button--container-disabled):hover,
.button--container-raised:not(.button--container-disabled):focus {
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, .12)
}

.button--container-raised:not(.button--container-disabled):active {
  box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, .12);
}

.button--container.button--container-disabled {
  background-color: #EEE;
  border-color: rgba(0, 0, 0, 0.54);
  color: rgba(0, 0, 0, 0.54);
}

.button--container-full_width {
  width: 100%;
}

.button--container-icon .button--label {
  font-size: 1.25em;
  line-height: inherit;
}

.button--container-icon.button--container-error .button--label {
  color: rgb(var(--error-color,  183, 28, 28));
}

.button--ripple {
  border-radius: 4px;
  height: 100%;
  left: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  width: 100%;
}

.button--container-icon .button--ripple {
  overflow: visible;
}

.card--action_btn .button--ripple {
  border-radius: 0;
}

.button--ripple::before {
  background-color: rgb(var(--primary-color, 0, 121, 102));
  content: '';
  height: 100%;
  left: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  transition: opacity 15ms linear, background-color 15ms linear;
  width: 100%;
  z-index: 1;
}

.button--container-icon .button--ripple::before {
  border-radius: 50%;
}

.sheet--container:not(.sheer--container-active) .button--container-icon.sheet--handle .button--ripple::before {
  opacity: 0.1;
}

.sheet--container.sheet-show_over_header:not(.sheer--container-active) .button--container-icon.sheet--handle .button--ripple::before {
  background-color: rgb(var(--bg-color, 255, 255, 255));
}

.button--container-outline .button--ripple::before,
.button--container-text .button--ripple::before {
  background-color: rgb(var(--primary-color, 0, 121, 102));
}

.button--container:not(.button--container-disabled):hover .button--ripple::before {
  opacity: 0.1;
}

.sheet--container:not(.sheer--container-active) .button--container.sheet--handle:not(.button--container-disabled):hover .button--ripple::before {
  opacity: 0.2;
}

.button--ripple::after {
  background-color: rgb(var(--bg-color, 255, 255, 255));
  border-radius: 50%;
  content: '';
  height: var(--ripple-size, 50%);
  left: calc(var(--ripple-size, 50%) / -2);
  position: absolute;
  top: calc(var(--ripple-size, 50%) / -2);
  transform: scale(0);
  transform-origin: 50% 50%;
  width: var(--ripple-size, 50%);
}

.button--container-icon .button--ripple::after {
  background-color: rgb(var(--primary-color, 0, 121, 102));
}

.button--container-outline .button--ripple::after,
.button--container-text .button--ripple::after {
  background-color: rgb(var(--primary-color, 0, 121, 102));
}

.button--container.button--container-activate:not(.button--container-disabled) .button--ripple::after {
  animation: button-ripple-scale-in 225ms forwards;
}

.button--container.button--container-deactivate:not(.button--container-disabled) .button--ripple::after {
  animation: button-ripple-opacity-out 150ms;
  opacity: 0;
  transform: translateX(var(--ripple-pos-x, 50%)) translateY(var(--ripple-pos-y, 50%)) scale(var(--ripple-scale, 1));
}

@keyframes button-ripple-scale-in {
  from {
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0;
    transform: translateX(var(--ripple-pos-x, 50%)) translateY(var(--ripple-pos-y, 50%)) scale(var(--ripple-init-scale, 0));
  }
  35% {
    opacity: var(--ripple-opacity, 1);
  }
  to {
    opacity: var(--ripple-opacity, 1);
    transform: translateX(var(--ripple-pos-x, 50%)) translateY(var(--ripple-pos-y, 50%)) scale(var(--ripple-scale, 1));
  }
}

/* @keyframes button-ripple-opacity-in {
	from {
		animation-timing-function	:	linear;
		opacity						:	0;
	}
	to {
		opacity	:	var(--ripple-opacity, 1);
	}
} */

@keyframes button-ripple-opacity-out {
  from {
    opacity: var(--ripple-opacity, 1);
  }
  to {
    opacity: 0;
  }
}


/*BUTTON LOADING ANIMATION CODE*/
.form--container .button--container-submit,
.button--container.button--container-loading {
  justify-self: center;
  width: 100%;
  -webkit-transition: width 350ms ease-in;
  transition: width 350ms ease-in;
}

.form--container.form--container-loading .button--container-submit,
.button--container.button--container-loading {
  width: var(--button-width, 300px);
  background-position: 0 0;
  background-color: transparent;
  background-repeat: repeat-x;
  background-image: linear-gradient(90deg, rgba(var(--primary-color, 0, 121, 102), 0.87) 0%, rgba(var(--primary-color, 0, 121, 102), 0.87) 25%, rgba(var(--primary-color, 0, 121, 102), 0.44) 25%, rgba(var(--primary-color, 0, 121, 102), 0.54) 50%, rgba(var(--primary-color, 0, 121, 102), 0.34) 50%, rgba(var(--primary-color, 0, 121, 102), 0.34) 75%, rgba(var(--primary-color, 0, 121, 102), 0.54) 75%, rgba(var(--primary-color, 0, 121, 102), 0.54) 100%);
  animation: button--container-submit 5000ms linear infinite;
  -webkit-transition: width 150ms ease-out;
  transition: width 150ms ease-out;
}

.form--container.form--container-loading .button--container-submit.button--container-disabled,
.button--container.button--container-loading.button--container-disabled {
  background-image: linear-gradient(90deg, rgba(0, 0, 0, 0.87) 0%, rgba(0, 0, 0, 0.87) 25%, rgba(0, 0, 0, 0.44) 25%, rgba(0, 0, 0, 0.54) 50%, rgba(0, 0, 0, 0.34) 50%, rgba(0, 0, 0, 0.34) 75%, rgba(0, 0, 0, 0.54) 75%, rgba(0, 0, 0, 0.54) 100%);
}

.form--container .button--container-submit .button--label::before,
.button--container.button--container-loading .button--label::before {
  content: 'Loading...';
  font-size: 0;
  font-weight: bold;
  letter-spacing: 0.4em;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.54);
}

.form--container:not(.form--container-loading) .button--container-submit .button--label,
.button--container:not(.button--container-loading) .button--label {
  -webkit-transition: font-size 350ms ease-out;
  transition: font-size 350ms ease-out;
}

.form--container.form--container-loading .button--container-submit .button--label,
.button--container.button--container-loading .button--label {
  font-size: 0;
}

.form--container.form--container-loading .button--container-submit .button--label::before,
.button--container.button--container-loading .button--label::before {
  font-size: 1rem;
  -webkit-transition: font-size 150ms ease-out;
  transition: font-size 150ms ease-out;
}

@keyframes button--container-submit {
  from {
    background-position: 0 0;
  }
  to {
    background-position: var(--button-width, 300px) 0;
  }
}




/*MODS FOR SHEETS*/
.sheet--handle {
  color: rgba(0, 0, 0, 0.54);
  position: absolute;
  top: 0.5em;
  left: 0.5em;
  text-decoration: none;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 5;
  font-size: 1.5em;
}

.sheet--handle.button--container {
  color: rgba(var(--bg-color, 0, 0, 0), 0.87);
}

.sheet--container.sheet--container-active .sheet--handle.button--container {
  color: rgba(var(--primary-color, 0, 0, 0), 0.87);
}

.sheet--handle:not(.button--container)::before {
  content: 'more_vert';
}

.sheet--container.sheet--container-active .sheet--handle:not(.button--container)::before {
  content: 'close';
}

.sheet--handle.button--container .button--label:before {
  content: 'more_vert';
}

.sheet--container.sheet--container-active .sheet--handle.button--container .button--label::before {
  content: 'close';
}

.sheet--handle:hover {
  color: rgba(var(--bg-color, 0, 0, 0), 0.87);
}

.sheet--container.sheet--container-right .sheet--handle {
  left: auto;
  right: 0.5em;
}
</style>